import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, DataTable, DataTablePagination, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, TooltipProvider, Tooltip, TooltipTrigger, TooltipContent, } from '@cargomatic/ui';
import { getFilterParams, useLaneRates } from 'hooks/useLaneRate';
import { showToast } from 'stores/ToastStore';
import WebAPIUtils from 'utils/AccessorialRequestWebAPIUtils';
import { t } from 'i18next';
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from '@tanstack/react-table';
import useModal from 'hooks/useModal';
import { ArrowRightIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, MoreHorizontal, } from 'lucide-react';
import AuditTrailModal from 'components/modules/pricing/modals/LaneRateAuditTrailModal';
import { LaneRatesCsvUploadModal } from 'components/modules/pricing/LaneRatesCsvUploadModal';
import qs from 'qs';
import { getColumns } from './columns';
import { EditRateSequenceModal } from './EditRateSequenceModal';
import { DataTableToolbar } from './DataTableToolbar';
import SessionStore from 'stores/SessionStore';
import { useLocalStorage } from 'hooks/useLocalStorage';
const highlighted = {
    rateCustomer: true,
    rateMarket: true,
    rateEquipmentType: true,
    rateEquipmentSize: true,
    rateHazardous: true,
    rateOrderType: true,
    rateSteamshipOrRailroad: true,
};
const paginationDefaults = {
    pageSize: 10,
    pageIndex: 0,
};
const columnVisibilityDefaults = {
    rateCustomer: false,
    rateEquipmentType: false,
    rateEquipmentSize: false,
    rateHazardous: false,
    rateOrderType: false,
    rateSteamshipOrRailroad: false,
};
export default function LaneRatesRateConditions() {
    const columns = getColumns();
    const [hideRateConditionColumns, setHideRateConditionColumns] = useState(true);
    const [columnVisibility, setColumnVisibility] = useState(Object.assign({}, columnVisibilityDefaults));
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState(Object.assign({}, paginationDefaults));
    const { visible, show, close } = useModal();
    const [filters, setFilters] = useState([]);
    const [selectedRateSequence, setSelectedRateSequence] = useState(null);
    const [rowSelection, setRowSelection] = useState({});
    const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
    const [rateAudit, setRateAudit] = useState(null);
    const [showLaneRatesCsvUploadModal, setLaneRatesCsvUploadModalIsOpen] = useState(false);
    const [isAbleToExport, setIsAbleToExport] = useState(true);
    const [tableState, setTableState] = useLocalStorage('laneRateRateConditionTableState');
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        setFilters((_a = tableState === null || tableState === void 0 ? void 0 : tableState.filters) !== null && _a !== void 0 ? _a : []);
        setSorting((_b = tableState === null || tableState === void 0 ? void 0 : tableState.sorting) !== null && _b !== void 0 ? _b : []);
        setPagination((_c = tableState === null || tableState === void 0 ? void 0 : tableState.pagination) !== null && _c !== void 0 ? _c : Object.assign({}, paginationDefaults));
        setColumnVisibility((_d = tableState === null || tableState === void 0 ? void 0 : tableState.columnVisibility) !== null && _d !== void 0 ? _d : Object.assign({}, columnVisibilityDefaults));
        setHideRateConditionColumns((_e = tableState === null || tableState === void 0 ? void 0 : tableState.hideRateConditionColumns) !== null && _e !== void 0 ? _e : true);
    }, []);
    useEffect(() => {
        setTableState({
            filters,
            sorting,
            pagination,
            columnVisibility,
            hideRateConditionColumns,
        });
    }, [
        filters,
        sorting,
        pagination,
        columnVisibility,
        hideRateConditionColumns,
    ]);
    const history = useHistory();
    const { data, isLoading, refetch } = useLaneRates({
        sorting,
        pagination,
        filters,
    });
    const selectedRowCount = useMemo(() => {
        return Object.keys(rowSelection).length;
    }, [rowSelection]);
    function handleFiltersChange(updater) {
        const newFilters = updater(filters);
        setFilters(newFilters);
        setRowSelection({});
        setPagination(Object.assign(Object.assign({}, pagination), { pageIndex: 0 }));
    }
    useEffect(() => {
        updateCanExport(filters);
    }, [filters]);
    function updateCanExport(filters) {
        if (!filters || !filters.length) {
            return setIsAbleToExport(true);
        }
        const filterNames = filters.map((f) => f.id);
        const allowedFilters = ['customerId', 'origin', 'destination'];
        const hasDisallowed = filterNames.some((name) => !allowedFilters.includes(name));
        if (hasDisallowed) {
            return setIsAbleToExport(false);
        }
        return setIsAbleToExport(true);
    }
    const isShipperLanePricingManager = SessionStore.isShipperLanePricingManager();
    const isCarrierLanePricingManager = SessionStore.isCarrierLanePricingManager();
    const isAbleToEditLaneRate = SessionStore.isAbleToEditLaneRate();
    const isAbleToUploadCSVLaneRates = SessionStore.isAbleToUploadCSVLaneRates();
    function canUpdateLaneRate(selectedRateSequenceCustomerType, isShipperLanePricingManager, isCarrierLanePricingManager, isAbleToEditLaneRate) {
        if (!isShipperLanePricingManager && !isCarrierLanePricingManager)
            return false;
        if ((isShipperLanePricingManager || isCarrierLanePricingManager) &&
            !(isShipperLanePricingManager && isCarrierLanePricingManager)) {
            return ((selectedRateSequenceCustomerType === 'Shipper' &&
                isShipperLanePricingManager) ||
                (selectedRateSequenceCustomerType === 'Carrier' &&
                    isCarrierLanePricingManager));
        }
        return isAbleToEditLaneRate;
    }
    const canBulkEditLaneRates = useMemo(() => {
        var _a;
        const keys = Object.keys(rowSelection).map((key) => parseInt(key, 10));
        return ((_a = data === null || data === void 0 ? void 0 : data.laneRates) !== null && _a !== void 0 ? _a : [])
            .filter((_, index) => keys.includes(index))
            .map((row) => row.customerType)
            .every((customerType) => {
            return canUpdateLaneRate(customerType, isShipperLanePricingManager, isCarrierLanePricingManager, isAbleToEditLaneRate);
        });
    }, [
        data === null || data === void 0 ? void 0 : data.laneRates,
        rowSelection,
        isShipperLanePricingManager,
        isCarrierLanePricingManager,
        isAbleToEditLaneRate,
    ]);
    function handleRowSelectionChange(selection) {
        setRowSelection(selection);
    }
    function handleSortChange(updater) {
        const newValue = updater(sorting);
        setSorting(newValue);
        setPagination({ pageSize: pagination.pageSize, pageIndex: 0 });
        setRowSelection({});
    }
    function onEditRateSequence(laneRate) {
        setSelectedRateSequence(laneRate);
        show();
    }
    function handleResequenceRatesSuccess() {
        refetch();
        close();
    }
    // Once we update the edit page to use query strings, update to a link with lane rate ids
    function onEditLaneRate(laneRate) {
        const queryString = qs.stringify({ id: laneRate.id });
        history.push({
            pathname: '/lane-rates/edit',
            search: `?${queryString}`,
        });
    }
    function onBulkEditRateSequence() {
        const selectedLaneRates = Object.keys(rowSelection).map((index) => {
            var _a, _b, _c;
            return Object.assign(Object.assign({}, (_a = data === null || data === void 0 ? void 0 : data.laneRates) === null || _a === void 0 ? void 0 : _a[index]), { weeklyCommitment: (_c = (_b = data === null || data === void 0 ? void 0 : data.laneRates) === null || _b === void 0 ? void 0 : _b[index]) === null || _c === void 0 ? void 0 : _c.commitment });
        });
        const selectedLaneRateIds = selectedLaneRates.map((lr) => lr.id);
        const searchParams = new URLSearchParams();
        selectedLaneRateIds.forEach((id) => searchParams.append('id', id));
        const queryString = searchParams.toString();
        history.push({
            pathname: '/lane-rates/edit',
            search: `?${queryString}`,
        });
    }
    function handleAuditClick(laneRate) {
        setRateAudit(laneRate);
        setShowAuditTrailModal(true);
    }
    function handleBulkUploadClick() {
        setLaneRatesCsvUploadModalIsOpen(true);
    }
    async function exportLaneRates() {
        const searchParams = getFilterParams({
            sorting: [{ id: 'sequenceDate' }],
            pagination,
            filters,
        });
        try {
            await WebAPIUtils.exportUserLaneRatesWithConditions(searchParams);
            showToast(t('CSV_DOWNLOAD_SUCCESSFUL'), null, 'success');
        }
        catch (error) {
            showToast(t('CSV_DOWNLOAD_FAILED'), error, 'error');
        }
    }
    function renderViewRateCard(laneRate) {
        const requiredDoc = (laneRate === null || laneRate === void 0 ? void 0 : laneRate.document) || (laneRate === null || laneRate === void 0 ? void 0 : laneRate.oldDocument);
        const url = (requiredDoc === null || requiredDoc === void 0 ? void 0 : requiredDoc.signedUrl) || '';
        const docMissing = Object.keys(requiredDoc).length === 0;
        if (!docMissing) {
            return (React.createElement(DropdownMenuItem, null,
                React.createElement(Link, { to: { pathname: url }, target: "_blank", className: `text-black` }, t('VIEW_RATE_CARD'))));
        }
        return (React.createElement(TooltipProvider, null,
            React.createElement(Tooltip, null,
                React.createElement(TooltipTrigger, { asChild: true },
                    React.createElement("span", { tabIndex: 0 },
                        React.createElement(DropdownMenuItem, null,
                            React.createElement(Link, { to: { pathname: url }, target: "_blank", className: `text-black ${docMissing ? 'pointer-events-none opacity-50' : ''}` }, t('VIEW_RATE_CARD'))))),
                React.createElement(TooltipContent, null, "Rate card has not been uploaded."))));
    }
    function canUpdateRow(row) {
        var _a, _b;
        if (isCarrierLanePricingManager && isShipperLanePricingManager) {
            return true;
        }
        if (isShipperLanePricingManager &&
            ((_a = row === null || row === void 0 ? void 0 : row.original) === null || _a === void 0 ? void 0 : _a.customerType) == 'Shipper') {
            return true;
        }
        if (isCarrierLanePricingManager &&
            ((_b = row === null || row === void 0 ? void 0 : row.original) === null || _b === void 0 ? void 0 : _b.customerType) == 'Carrier') {
            return true;
        }
        return false;
    }
    const mappedColumnDefs = useMemo(() => {
        return columns.concat({
            id: 'actions',
            cell: ({ row }) => {
                const payment = row.original;
                return (React.createElement("div", { className: "text-right" },
                    React.createElement(DropdownMenu, { modal: false },
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { variant: "ghost", className: "w-8 h-8 p-0" },
                                React.createElement("span", { className: "sr-only" }, t('OPEN_MENU')),
                                React.createElement(MoreHorizontal, { className: "w-4 h-4" }))),
                        React.createElement(DropdownMenuContent, { align: "end" },
                            React.createElement(DropdownMenuLabel, null, t('ACTIONS')),
                            !canUpdateRow(row) && (React.createElement(React.Fragment, null,
                                React.createElement(TooltipProvider, null,
                                    React.createElement(Tooltip, null,
                                        React.createElement(TooltipTrigger, { asChild: true },
                                            React.createElement("span", { tabIndex: 0 },
                                                React.createElement(DropdownMenuItem, { disabled: !canUpdateRow(row), onClick: () => onEditRateSequence(payment) }, t('EDIT_RATE_SEQUENCE')))),
                                        React.createElement(TooltipContent, null, "You do not have permission to edit the rate sequence."))),
                                React.createElement(TooltipProvider, null,
                                    React.createElement(Tooltip, null,
                                        React.createElement(TooltipTrigger, { asChild: true },
                                            React.createElement("span", { tabIndex: 0 },
                                                React.createElement(DropdownMenuItem, { onClick: () => onEditLaneRate(payment), disabled: !canUpdateRow(row) }, t('EDIT_LANE_RATE')))),
                                        React.createElement(TooltipContent, null, "You do not have permission to edit the lane rate."))))),
                            canUpdateRow(row) && (React.createElement(React.Fragment, null,
                                React.createElement(DropdownMenuItem, { onClick: () => onEditRateSequence(payment) }, t('EDIT_RATE_SEQUENCE')),
                                React.createElement(DropdownMenuItem, { onClick: () => onEditLaneRate(payment), disabled: !canUpdateRow(row) }, t('EDIT_LANE_RATE')))),
                            React.createElement(DropdownMenuSeparator, null),
                            renderViewRateCard(payment),
                            React.createElement(DropdownMenuItem, { onClick: () => handleAuditClick(payment) }, t('VIEW_AUDIT_TRAIL'))))));
            },
        });
    }, []);
    function handlePaginationChange(selection) {
        setPagination(selection);
        setRowSelection({});
    }
    const options = {
        columns: mappedColumnDefs,
        data: (data === null || data === void 0 ? void 0 : data.laneRates) || [],
        enableRowSelection: true,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            columnVisibility,
            sorting,
            pagination,
            rowSelection,
            columnFilters: filters,
        },
        onColumnVisibilityChange: setColumnVisibility,
        onSortingChange: handleSortChange,
        onPaginationChange: handlePaginationChange,
        onRowSelectionChange: handleRowSelectionChange,
        onColumnFiltersChange: handleFiltersChange,
    };
    const table = useReactTable(Object.assign(Object.assign({}, options), { manualSorting: true, manualPagination: true, rowCount: data === null || data === void 0 ? void 0 : data.count, manualFiltering: true }));
    useEffect(() => {
        if (hideRateConditionColumns) {
            setColumnVisibility({
                rateCustomer: false,
                rateEquipmentType: false,
                rateEquipmentSize: false,
                rateHazardous: false,
                rateOrderType: false,
                rateSteamshipOrRailroad: false,
            });
        }
        else {
            setColumnVisibility({
                rateCustomer: true,
                rateEquipmentType: true,
                rateEquipmentSize: true,
                rateHazardous: true,
                rateOrderType: true,
                rateSteamshipOrRailroad: true,
            });
        }
    }, [hideRateConditionColumns]);
    function renderFooter() {
        if (isLoading) {
            return null;
        }
        return React.createElement(DataTablePagination, { table: table });
    }
    function onToggleRateConditionsVisibility() {
        setHideRateConditionColumns((visibility) => !visibility);
    }
    function renderHeader() {
        return (React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: "text-lg font-semibold mb-4" }, t('LANE_RATES')),
            React.createElement("div", { className: "mt-2 flex justify-between flex-wrap" },
                React.createElement("div", { className: "flex-1" },
                    React.createElement(DataTableToolbar, { table: table },
                        React.createElement("div", { className: "flex justify-end" },
                            React.createElement("div", { className: "flex space-x-2 flex-wrap" },
                                React.createElement(Button, { onClick: onToggleRateConditionsVisibility, className: [
                                        'h-10',
                                        {
                                            'bg-slate-50': !hideRateConditionColumns,
                                        },
                                    ], size: "sm", variant: "outline" },
                                    hideRateConditionColumns
                                        ? t('EXPAND_RATE_CONDITIONS')
                                        : t('COLLAPSE_RATE_CONDITIONS'),
                                    hideRateConditionColumns ? (React.createElement(ChevronRightIcon, { className: "w-4 h-4" })) : (React.createElement(ChevronLeftIcon, { className: "w-4 h-4" }))),
                                React.createElement(DropdownMenu, { modal: false },
                                    React.createElement(DropdownMenuTrigger, { asChild: true },
                                        React.createElement(Button, { variant: "outline", className: "h-10" },
                                            t('ACTIONS'),
                                            ' ',
                                            React.createElement(ChevronDownIcon, { className: "ml-2 w-4 h-4" }))),
                                    React.createElement(DropdownMenuContent, { align: "end" },
                                        !isAbleToUploadCSVLaneRates && (React.createElement(TooltipProvider, null,
                                            React.createElement(Tooltip, null,
                                                React.createElement(TooltipTrigger, { asChild: true },
                                                    React.createElement("span", { tabIndex: 0 },
                                                        React.createElement(DropdownMenuItem, { disabled: !isAbleToUploadCSVLaneRates, className: `pointer-events-none opacity-50` }, t('UPLOAD_LANE_RATES')))),
                                                React.createElement(TooltipContent, null, "You do not have permission to upload.")))),
                                        isAbleToUploadCSVLaneRates && (React.createElement(DropdownMenuItem, { onClick: () => handleBulkUploadClick() }, t('UPLOAD_LANE_RATES'))),
                                        !(isShipperLanePricingManager ||
                                            isCarrierLanePricingManager) && (React.createElement(TooltipProvider, null,
                                            React.createElement(Tooltip, null,
                                                React.createElement(TooltipTrigger, { asChild: true },
                                                    React.createElement("span", { tabIndex: 0 },
                                                        React.createElement(DropdownMenuItem, { disabled: true }, t('ADD_LANE_RATE')))),
                                                React.createElement(TooltipContent, null, "You do not have permission to add a lane rate.")))),
                                        (isShipperLanePricingManager ||
                                            isCarrierLanePricingManager) && (React.createElement(DropdownMenuItem, { asChild: true },
                                            React.createElement(Link, { to: "/lane-rates/add", className: "text-black" }, t('ADD_NEW_LANE_RATE')))),
                                        !isAbleToExport && (React.createElement(TooltipProvider, null,
                                            React.createElement(Tooltip, null,
                                                React.createElement(TooltipTrigger, { asChild: true },
                                                    React.createElement("span", { tabIndex: 0 },
                                                        React.createElement(DropdownMenuItem, { disabled: !isAbleToExport }, t('EXPORT_LANE_RATES')))),
                                                React.createElement(TooltipContent, null, "Export only allowed for customer, origin, and destination filters.")))),
                                        isAbleToExport && (React.createElement(DropdownMenuItem, { onClick: exportLaneRates }, t('EXPORT_LANE_RATES')))))))))),
            selectedRowCount > 0 && (React.createElement(Alert, { className: "mt-4 text-sm bg-blue-50" },
                React.createElement("div", { className: "flex justify-between items-center" },
                    React.createElement("span", null,
                        selectedRowCount,
                        " ",
                        t('ROWS_SELECTED'),
                        "."),
                    !canBulkEditLaneRates && (React.createElement(TooltipProvider, null,
                        React.createElement(Tooltip, null,
                            React.createElement(TooltipTrigger, { asChild: true },
                                React.createElement("span", { tabIndex: 0 },
                                    React.createElement(Button, { disabled: !canBulkEditLaneRates, variant: "link", onClick: () => onBulkEditRateSequence() },
                                        t('EDIT_LANE_RATES'),
                                        React.createElement(ArrowRightIcon, { className: "ml-2 w-4 h-4" })))),
                            React.createElement(TooltipContent, null, "You do not have permission to edit.")))),
                    canBulkEditLaneRates && (React.createElement(Button, { variant: "link", onClick: () => onBulkEditRateSequence() },
                        t('EDIT_LANE_RATES'),
                        React.createElement(ArrowRightIcon, { className: "ml-2 w-4 h-4" }))))))));
    }
    return (React.createElement("div", { className: "px-4 py-5 sm:p-6 mx-auto" },
        React.createElement(Card, null,
            renderHeader(),
            React.createElement(CardContent, { className: "space-y-4" },
                React.createElement(DataTable, { table: table, isLoading: isLoading, highlighted: highlighted }),
                renderFooter())),
        React.createElement(AuditTrailModal, { show: showAuditTrailModal, rate: rateAudit, onHide: () => setShowAuditTrailModal(false), type: "laneRates" }),
        showLaneRatesCsvUploadModal && (React.createElement(LaneRatesCsvUploadModal, { onClose: () => setLaneRatesCsvUploadModalIsOpen(false) })),
        visible && (React.createElement(EditRateSequenceModal, { laneRate: selectedRateSequence, open: visible, onOpenChange: close, onSaveSuccess: handleResequenceRatesSuccess }))));
}
